import { Link } from "react-router-dom";
import { STATUS, ACCESS } from "@/lib/format";
import { ArrowRight } from "lucide-react";

export const StatusBadge = ({ status, testid }) => {
  const s = STATUS[status] || { label: status, color: "#94A3B8" };
  return (
    <span data-testid={testid} className="inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide" style={{ color: s.color, background: `${s.color}1f`, border: `1px solid ${s.color}55` }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: s.color }} />
      {s.label}
    </span>
  );
};

export const AccessBadge = ({ access }) => {
  const a = ACCESS[access];
  return <span className="rounded px-2 py-0.5 text-[10.5px] font-bold" style={{ color: a.color, background: `${a.color}1f` }}>{a.label}</span>;
};

export const Pill = ({ color, children, testid }) => (
  <span data-testid={testid} className="inline-block rounded px-2 py-0.5 text-[11px] font-bold" style={{ color: "#06142A", background: color }}>{children}</span>
);

export const SyntheticTag = ({ text = "Synthetic data" }) => (
  <span className="inline-flex items-center rounded border border-purple-400/40 bg-purple-400/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-purple-300">{text}</span>
);

export const Panel = ({ title, right, children, className = "", testid, bodyClass = "p-4" }) => (
  <section data-testid={testid} className={`vig-panel ${className}`}>
    {title && (
      <header className="flex items-center justify-between gap-3 px-4 pt-3.5">
        <h2 className="vig-label">{title}</h2>
        {right}
      </header>
    )}
    <div className={bodyClass}>{children}</div>
  </section>
);

export const Kpi = ({ icon: Icon, value, label, sub, badge, testid }) => (
  <div data-testid={testid} className="flex items-start gap-3 px-4 py-3">
    {Icon && <Icon className="h-8 w-8 shrink-0 text-[#F4B842]" strokeWidth={1.4} />}
    <div className="min-w-0">
      <div className="text-[22px] font-bold leading-tight text-white">{value}</div>
      <div className="text-[12px] leading-snug text-slate-300">{label}</div>
      {sub && <div className="text-[11px] text-slate-400">{sub}</div>}
      {badge && <div className="mt-1">{badge}</div>}
    </div>
  </div>
);

export const KpiStrip = ({ children, testid }) => (
  <div data-testid={testid} className="vig-glass grid grid-cols-2 divide-slate-500/20 md:flex md:divide-x [&>*]:flex-1">{children}</div>
);

export const Tabs = ({ items, value, onChange, testid }) => (
  <div data-testid={testid} className="flex flex-wrap gap-1 rounded-md border border-slate-500/20 bg-[#06142a]/60 p-1">
    {items.map((it) => {
      const [id, label] = Array.isArray(it) ? it : [it, it];
      return (
        <button key={id} data-testid={`${testid}-${id}`} data-active={value === id} className="vig-tab" onClick={() => onChange(id)}>{label}</button>
      );
    })}
  </div>
);

export const Progress = ({ value, color = "#0EA5A4" }) => (
  <div className="h-1.5 w-full overflow-hidden rounded bg-slate-700/60">
    <div className="h-full rounded transition-[width] duration-500" style={{ width: `${Math.max(0, Math.min(100, value))}%`, background: color }} />
  </div>
);

export const LinkArrow = ({ to, children, testid }) => (
  <Link to={to} data-testid={testid} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#F4B842] hover:text-[#F8D27A]">
    {children} <ArrowRight className="h-3.5 w-3.5" />
  </Link>
);

export const Restricted = ({ title, message, onSwitch, switchLabel, testid = "restricted-panel" }) => (
  <div data-testid={testid} className="vig-panel mx-auto my-16 max-w-xl p-8 text-center">
    <div className="vig-label mb-2 text-[#F4B842]">Access controlled</div>
    <h2 className="text-2xl font-bold">{title}</h2>
    <p className="mt-2 text-sm text-slate-300">{message}</p>
    {onSwitch && <button data-testid="restricted-switch-role-btn" onClick={onSwitch} className="vig-btn-gold mt-6 px-5 py-2.5 text-sm">{switchLabel}</button>}
  </div>
);
